// src/components/Captcha.tsx
import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { RotateCcw } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../lib/i18n';

interface CaptchaProps {
  onValidate: (isValid: boolean) => void;
}

export interface CaptchaHandle {
  reset: () => void;
}

export const Captcha = forwardRef<CaptchaHandle, CaptchaProps>(({ onValidate }, ref) => {
  const { language } = useLanguage();
  const [num1, setNum1] = useState(0);
  const [num2, setNum2] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [translations, setTranslations] = useState<any>({});

  useEffect(() => {
    const loadTranslations = async () => {
      const formTranslations = await getTranslation(language, 'form');
      setTranslations(formTranslations);
    };
    loadTranslations();
  }, [language]);

  const generateNumbers = () => {
    setNum1(Math.floor(Math.random() * 10));
    setNum2(Math.floor(Math.random() * 10));
    setUserAnswer('');
    setIsCorrect(null);
    onValidate(false);
  };

  useEffect(() => {
    generateNumbers();
  }, []);

  useImperativeHandle(ref, () => ({
    reset: generateNumbers
  }));

  const checkAnswer = (value: string) => {
    const answer = parseInt(value, 10);
    const correct = answer === num1 + num2;
    setIsCorrect(correct);
    onValidate(correct);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUserAnswer(value);
    if (value) {
      checkAnswer(value);
    } else {
      setIsCorrect(null);
      onValidate(false);
    }
  };

  if (!translations.captcha) {
    return null;
  }

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2 text-bamboo-600 px-3 py-2 rounded">
        <span>{num1}</span>
        <span>+</span>
        <span>{num2}</span>
        <span>=</span>
      </div>

      <input
        type="number"
        value={userAnswer}
        onChange={handleChange}
        placeholder={translations.captcha.placeholder}
        className={`w-24 px-3 py-2 rounded border text-gray-900 ${
          isCorrect === null
            ? 'border-gray-300'
            : isCorrect
            ? 'border-bamboo-500 bg-bamboo-50'
            : 'border-red-500'
        } focus:outline-none focus:ring-2 ${
          isCorrect === null
            ? 'focus:ring-bamboo-200'
            : isCorrect
            ? 'focus:ring-bamboo-200'
            : 'focus:ring-red-200'
        }`}
      />

      <button
        type="button"
        onClick={generateNumbers}
        className="text-bamboo-600 hover:text-bamboo-700 p-2"
      >
        <RotateCcw size={20} />
      </button>
    </div>
  );
});

Captcha.displayName = 'Captcha';
